import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Clock, Check, Trash2, Plus, HelpCircle, Pencil, GripVertical, FileDown, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { API } from '@/App';

const AUDIENCES = [
  { value: 'member', label: 'For members' },
  { value: 'practitioner', label: 'For practitioners' },
  { value: 'vendor', label: 'For vendors' },
];

/**
 * Admin · Q&A — the queue of what people have asked, and the answers already up.
 *
 * Waiting questions come first, because an unanswered question is the only thing here that
 * needs him. Answering one publishes it and tells whoever asked.
 */
export default function AdminQATab() {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [entries, setEntries] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newEntry, setNewEntry] = useState({ audience: 'member', question: '', answer: '' });
  const [editing, setEditing] = useState(null);      // { id, question, answer, audience }
  const [downloading, setDownloading] = useState(null);
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/qa/admin/all`, { headers });
      setEntries(res.data.entries || []);
      setPendingCount(res.data.pending_count || 0);
    } catch { toast.error('Could not load the Q&A'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publish = async (entry) => {
    const answer = drafts[entry.id] ?? entry.answer ?? '';
    if (!answer.trim()) return toast.error('Write the answer first');
    setBusy(entry.id);
    try {
      await axios.put(`${API}/qa/admin/${entry.id}`,
        { answer: answer.trim(), publish: true }, { headers });
      toast.success('Answered and published');
      setDrafts(d => ({ ...d, [entry.id]: undefined }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save that"); }
    finally { setBusy(null); }
  };

  const remove = async (entry, confirmFirst = false) => {
    // A published answer is work he wrote; one stray click should not take it
    if (confirmFirst && !window.confirm(
      `Delete this answer for good?\n\n"${entry.question}"\n\nMembers will no longer see it.`)) {
      return;
    }
    setBusy(entry.id);
    try {
      await axios.delete(`${API}/qa/admin/${entry.id}`, { headers });
      load();
    } catch { toast.error("Couldn't remove that"); }
    finally { setBusy(null); }
  };

  const create = async () => {
    if (!newEntry.question.trim() || !newEntry.answer.trim()) {
      return toast.error('A question and an answer');
    }
    setBusy('new');
    try {
      await axios.post(`${API}/qa/admin`, newEntry, { headers });
      toast.success('Added');
      setNewEntry({ audience: 'member', question: '', answer: '' });
      setAdding(false);
      load();
    } catch { toast.error("Couldn't add that"); }
    finally { setBusy(null); }
  };

  /** Revise what is already up — the platform moves on and old answers mislead people. */
  const saveEdit = async () => {
    if (!editing.question.trim() || !editing.answer.trim()) {
      return toast.error('A question and an answer');
    }
    setBusy(editing.id);
    try {
      await axios.put(`${API}/qa/admin/${editing.id}`, {
        question: editing.question.trim(),
        answer: editing.answer.trim(),
        audience: editing.audience,
      }, { headers });
      toast.success('Revised — members see the new wording straight away');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that revision");
    } finally { setBusy(null); }
  };

  /** Drop a question into a new place in its own section. */
  const drop = async (audience, rows, toIndex) => {
    const fromIndex = dragFrom.current?.index;
    dragFrom.current = null;
    setDragOver(null);
    if (fromIndex === undefined || fromIndex === toIndex) return;
    const next = [...rows];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setEntries(all => {           // show it immediately, then confirm
      const others = all.filter(e => !(e.status === 'published' && e.audience === audience));
      return [...others, ...next.map((e, i) => ({ ...e, order: i }))];
    });
    try {
      await axios.put(`${API}/qa/admin/reorder`,
        { audience, entry_ids: next.map(e => e.id) }, { headers });
      toast.success('Order saved');
    } catch {
      toast.error("Couldn't save that order");
    }
    load();
  };

  const downloadWord = async (audience, label) => {
    setDownloading(audience);
    try {
      const res = await axios.get(`${API}/qa/admin/export?audience=${audience}`,
        { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Questions and answers - ${label.replace('For ', '')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (err) {
      const detail = err.response?.status === 404
        ? 'Nothing published in that section yet' : "Couldn't build that document";
      toast.error(detail);
    } finally { setDownloading(null); }
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const pending = entries.filter(e => e.status === 'pending');
  const published = entries.filter(e => e.status === 'published');

  return (
    <div className="space-y-6" data-testid="admin-qa-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
            <HelpCircle className="w-4 h-4" /> Questions &amp; Answers
          </h3>
          <p className="text-sm text-gray-200">
            Answer a question once and it stays answered. Publishing emails the full answer to
            whoever asked. {pendingCount > 0
              ? `${pendingCount} waiting on you.`
              : 'Nothing waiting.'}
          </p>
        </div>
        <Button variant="outline" onClick={() => setAdding(a => !a)}
                className="gap-2 border-white/25 text-gray-100 hover:bg-white/10"
                data-testid="admin-qa-add-toggle">
          <Plus className="w-4 h-4" /> Write an entry
        </Button>
      </div>

      {adding && (
        <div className="rounded-lg border border-gray-700 bg-slate-900/85 backdrop-blur-sm p-4 space-y-3" data-testid="admin-qa-new">
          <div>
            <Label className="text-gray-200">Audience</Label>
            <select
              className="w-full mt-1 bg-slate-950 border border-gray-600 rounded-md p-2 text-sm text-gray-100"
              value={newEntry.audience}
              onChange={e => setNewEntry(n => ({ ...n, audience: e.target.value }))}
              data-testid="admin-qa-new-audience"
            >
              {AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-gray-200">Question</Label>
            <input
              className="w-full mt-1 bg-slate-950/80 border border-gray-600 rounded-md p-2 text-sm text-gray-100 placeholder:text-gray-400"
              value={newEntry.question}
              onChange={e => setNewEntry(n => ({ ...n, question: e.target.value }))}
              data-testid="admin-qa-new-question"
            />
          </div>
          <div>
            <Label className="text-gray-200">Answer</Label>
            <textarea
              rows={4}
              className="w-full mt-1 bg-slate-950/80 border border-gray-600 rounded-md p-2 text-sm text-gray-100 placeholder:text-gray-400"
              value={newEntry.answer}
              onChange={e => setNewEntry(n => ({ ...n, answer: e.target.value }))}
              data-testid="admin-qa-new-answer"
            />
          </div>
          <Button onClick={create} disabled={busy === 'new'} data-testid="admin-qa-new-save">
            {busy === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publish it'}
          </Button>
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Waiting on an answer
          </h4>
          {pending.map(e => (
            <div key={e.id} className="rounded-lg border border-amber-600/40 bg-slate-900/85 backdrop-blur-sm p-4 space-y-2"
                 data-testid={`admin-qa-pending-${e.id}`}>
              <p className="text-sm font-semibold text-white">{e.question}</p>
              <p className="text-xs text-gray-300">
                Asked by {e.asked_by_name || 'someone'} · {AUDIENCES.find(a => a.value === e.audience)?.label}
              </p>
              <textarea
                rows={4}
                className="w-full bg-slate-950/80 border border-gray-600 rounded-md p-2 text-sm text-gray-100 placeholder:text-gray-400"
                placeholder="Your answer — plain words, no jargon"
                value={drafts[e.id] ?? ''}
                onChange={ev => setDrafts(d => ({ ...d, [e.id]: ev.target.value }))}
                data-testid={`admin-qa-answer-input-${e.id}`}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => publish(e)} disabled={busy === e.id} className="gap-1"
                        data-testid={`admin-qa-publish-${e.id}`}>
                  {busy === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Answer &amp; publish
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(e)} disabled={busy === e.id}
                        data-testid={`admin-qa-delete-${e.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-white">
          Published ({published.length})
        </h4>
        <p className="text-xs text-gray-300">
          Drag a question by its handle to set the order members read them in. Edit any entry to
          keep it true as the platform changes.
        </p>
        {AUDIENCES.map(a => {
          const rows = published
            .filter(e => e.audience === a.value)
            .sort((x, y) => (x.order ?? 100) - (y.order ?? 100));
          return (
            <div key={a.value} className="space-y-1" data-testid={`admin-qa-section-${a.value}`}>
              <div className="flex items-center justify-between gap-3 mt-4">
                <p className="text-xs uppercase tracking-wide text-gray-300">{a.label}</p>
                <Button size="sm" variant="outline"
                        className="gap-1.5 h-7 text-xs border-white/25 text-gray-100 hover:bg-white/10"
                        onClick={() => downloadWord(a.value, a.label)}
                        disabled={downloading === a.value}
                        data-testid={`admin-qa-download-${a.value}`}>
                  {downloading === a.value
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <FileDown className="w-3 h-3" />}
                  Word document
                </Button>
              </div>
              {rows.length === 0 && (
                <p className="text-xs text-gray-400" data-testid={`admin-qa-empty-${a.value}`}>
                  Nothing published here yet.
                </p>
              )}
              {rows.map((e, i) => (
                <div key={e.id}
                     draggable={!editing}
                     onDragStart={() => { dragFrom.current = { audience: a.value, index: i }; }}
                     onDragOver={ev => {
                       if (dragFrom.current?.audience !== a.value) return;
                       ev.preventDefault();
                       setDragOver(`${a.value}-${i}`);
                     }}
                     onDragLeave={() => setDragOver(null)}
                     onDrop={ev => { ev.preventDefault(); drop(a.value, rows, i); }}
                     className={`rounded-lg border bg-slate-900/85 backdrop-blur-sm p-3 transition-colors ${
                       dragOver === `${a.value}-${i}`
                         ? 'border-amber-400/70 bg-amber-400/10'
                         : 'border-gray-700'}`}
                     data-testid={`admin-qa-published-${e.id}`}>
                  {editing?.id === e.id ? (
                    <div className="space-y-2" data-testid={`admin-qa-edit-${e.id}`}>
                      <div>
                        <Label className="text-gray-200 text-xs">Question</Label>
                        <input
                          className="w-full mt-1 bg-slate-950/80 border border-gray-600 rounded-md p-2 text-sm text-gray-100"
                          value={editing.question}
                          onChange={ev => setEditing(s => ({ ...s, question: ev.target.value }))}
                          data-testid={`admin-qa-edit-question-${e.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-gray-200 text-xs">Answer</Label>
                        <textarea
                          rows={5}
                          className="w-full mt-1 bg-slate-950/80 border border-gray-600 rounded-md p-2 text-sm text-gray-100"
                          value={editing.answer}
                          onChange={ev => setEditing(s => ({ ...s, answer: ev.target.value }))}
                          data-testid={`admin-qa-edit-answer-${e.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-gray-200 text-xs">Audience</Label>
                        <select
                          className="w-full mt-1 bg-slate-950 border border-gray-600 rounded-md p-2 text-sm text-gray-100"
                          value={editing.audience}
                          onChange={ev => setEditing(s => ({ ...s, audience: ev.target.value }))}
                          data-testid={`admin-qa-edit-audience-${e.id}`}
                        >
                          {AUDIENCES.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit} disabled={busy === e.id} className="gap-1"
                                data-testid={`admin-qa-edit-save-${e.id}`}>
                          {busy === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Save revision
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}
                                className="gap-1 border-white/25 text-gray-100 hover:bg-white/10"
                                data-testid={`admin-qa-edit-cancel-${e.id}`}>
                          <X className="w-3 h-3" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-4 h-4 mt-0.5 text-gray-400 cursor-grab shrink-0"
                                    data-testid={`admin-qa-drag-${e.id}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-100">{e.question}</p>
                        <p className="text-xs text-gray-300 mt-1 line-clamp-2">
                          {(e.answer || '').replace(/\*\*/g, '')}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost"
                              className="text-gray-200 hover:text-white hover:bg-white/10"
                              onClick={() => setEditing({
                                id: e.id, question: e.question || '', answer: e.answer || '',
                                audience: e.audience,
                              })}
                              data-testid={`admin-qa-edit-open-${e.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(e, true)} disabled={busy === e.id}
                              className="text-gray-200 hover:text-red-300 hover:bg-white/10"
                              data-testid={`admin-qa-remove-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
